export type AusschreibungStatus =
  | 'draft'
  | 'inprogress'
  | 'ready'
  | 'sent'
  | 'won'
  | 'lost';

/** Kontaktzeile (Supabase `contacts`), nach API-Enrichment als `contact` am Ausschreibungs-Objekt. */
export interface AusschreibungContactEmbed {
  id: string;
  name: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  company: string | null;
  notes: string | null;
  contact_type: string | null;
}

/**
 * Alt-Entwurf (JSON in `offer_draft`): Positionen aus der Zeit vor der Akte mit Vorgang.
 * Wird nur noch schreibgeschützt angezeigt; neue Akten halten das LV in `offering_positions`.
 */
export interface TenderRow {
  id: string;
  /** Original LV text from the tender. */
  tenderShort: string;
  tenderLong: string;
  tenderQty: number;
  tenderUnit: string;
  /** Internal mapping (Stammdaten / customer-facing). */
  internalShort: string;
  internalLong: string;
  internalQty: number;
  materialId: string | null;
  materialPrice: number;
  minutesPerUnit: number;
  confidence: number;
  /** Pro Zeile; KI-Zuweisung kann mit Zeile `[KI] …` ergänzen. */
  note: string;
  /** Nach KI-Zuweisung: Hinweis am LV-Kurztext (Tooltip). */
  tenderMatchWarning?: string | null;
  /** Stammdaten-Einheit für die interne Menge; sonst LV-Einheit anzeigen. */
  matchedMaterialUnit?: string | null;
  /** Großhändler / Lieferant des gematchten Materials. */
  supplierName?: string | null;
  /** Artikelnummer beim Großhändler / im Katalog. */
  articleNumber?: string | null;
  procurementSource?: 'catalog' | 'manufacturer' | 'unknown';
  /** Vom LV gefordertes Fabrikat/Hersteller (z. B. Viega, Geberit) — für die Beschaffungsliste. */
  manufacturer?: string | null;
}

export interface TenderOfferDraft {
  rows?: TenderRow[];
  realLaborRate?: number;
  laborRate?: number;
  vatRate?: number;
  materialMarkupRate?: number;
  /** Free-form schema version marker so we can migrate later. */
  version?: number;
}

/** Kennzahlen des LV im verknüpften Vorgang (`GET /ausschreibungen/:id`). */
export interface TenderLvSummary {
  offering_id: string;
  lead_id: string | null;
  item_count: number;
  title_count: number;
  priced_count: number;
  net_total: number;
}

export interface AusschreibungListItem {
  id: string;
  display_id: string | null;
  lead_id: string | null;
  offering_id?: string | null;
  contact_id: string | null;
  title: string;
  customer_name: string;
  customer_company: string;
  customer_street: string;
  customer_zip: string;
  customer_city: string;
  deadline: string | null;
  execution: string;
  status: AusschreibungStatus;
  created_at: string;
  updated_at: string;
  /** Nur in Listen-API gesetzt, für Sidebar-/Listen-Brutto (Alt-Entwürfe). */
  offer_draft?: TenderOfferDraft | null;
  /** Nach `enrichAusschreibungRow` — ersetzt das Roh-Embed `contacts`. */
  contact?: AusschreibungContactEmbed | null;
}

export interface AusschreibungDetail extends AusschreibungListItem {
  binding_period_days: number | null;
  ai_hints: string;
  internal_notes: string;
  offer_draft: TenderOfferDraft;
  is_deleted: boolean;
  /** Vor KI/ohne Kontakt: extrahierte Namensteile (mit Kontakt leer). */
  customer_first_name?: string | null;
  customer_last_name?: string | null;
  gaeb_document_id?: string | null;
  lv_imported_at?: string | null;
  lv_summary?: TenderLvSummary | null;
  /** Schritt „Großhändler anfragen“: null = offen, sonst erledigt. */
  supplier_request_state?: 'sent' | 'skipped' | null;
}

/** Eine verschickte Preisanfrage an einen Großhändler (je Empfänger). */
export interface TenderSupplierRequest {
  id: string;
  ausschreibung_id: string;
  organization_id: string | null;
  contact_id: string | null;
  supplier_name: string;
  email: string;
  recipient_name: string;
  subject: string;
  attachment_filename: string | null;
  gaeb_dp: string;
  status: 'pending' | 'sent' | 'failed';
  error: string | null;
  sent_at: string | null;
  created_at: string;
}

export type TenderDocKind = 'gaeb' | 'supplier_quote' | 'other';

export interface TenderDocumentClassification {
  supplierName?: string | null;
  quoteNumber?: string | null;
  quoteDate?: string | null;
  confidence?: number | null;
  reason?: string | null;
}

export interface TenderDocument {
  id: string;
  ausschreibung_id: string;
  filename: string;
  mime_type: string | null;
  size: number;
  created_at: string;
  page_count?: number | null;
  doc_kind?: TenderDocKind;
  doc_kind_source?: 'auto' | 'manual' | 'extension' | null;
  doc_label?: string | null;
  classification?: TenderDocumentClassification | null;
  /** Upload-Antwort: Datei war schon in der Akte (gleicher Inhalt). */
  duplicate?: boolean;
}

export type SupplierQuoteStatus = 'pending' | 'extracting' | 'ready' | 'failed';

export interface SupplierQuote {
  id: string;
  ausschreibung_id: string;
  offering_id: string | null;
  source_document_id: string | null;
  source_kind: 'pdf' | 'ugl' | 'manual';
  source_filename?: string | null;
  supplier_name: string;
  supplier_organization_id: string | null;
  supplier_id: string | null;
  quote_number: string | null;
  quote_date: string | null;
  valid_until: string | null;
  currency: string;
  price_basis: 'net' | 'list' | 'gross' | 'unknown';
  net_total: number | null;
  header_notes: string | null;
  status: SupplierQuoteStatus;
  status_error: string | null;
  attempts: number;
  extract_all_pages: boolean;
  page_count: number | null;
  pages_sent: number;
  pages_skipped: number;
  vision_pages: number;
  estimated_cost_eur: number;
  item_count: number;
  matched_item_count: number;
  extracted_at: string | null;
  matched_at: string | null;
  applied_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SupplierQuoteItem {
  id: string;
  supplier_quote_id: string;
  line_no: number;
  page: number | null;
  printed_line_no: string | null;
  lv_oz_ref: string | null;
  lv_oz_normalized: string | null;
  article_number: string | null;
  manufacturer: string | null;
  manufacturer_article_number: string | null;
  gtin: string | null;
  item_name: string;
  description: string;
  quantity: number | null;
  unit: string | null;
  unit_normalized: string | null;
  price_unit_qty: number;
  list_price: number | null;
  discount_percent: number | null;
  unit_price_net: number | null;
  line_total_net: number | null;
  tier_prices: Array<{ minQty: number; unitPriceNet: number }> | null;
  is_alternative: boolean;
  delivery_time: string | null;
  raw_text: string | null;
  extraction_confidence: number | null;
  supplier_material_id: string | null;
  offering_position_id: string | null;
  match_source: 'oz' | 'article' | 'gtin' | 'catalog' | 'llm' | 'manual' | null;
  match_confidence: number | null;
  qty_factor: number;
  match_role: 'primary' | 'component';
  match_status: 'proposed' | 'confirmed' | 'rejected' | null;
  match_note: string | null;
}

export const SUPPLIER_QUOTE_STATUS_LABELS: Record<SupplierQuoteStatus, string> = {
  pending: 'Warteschlange',
  extracting: 'wird gelesen',
  ready: 'gelesen',
  failed: 'Fehler',
};

export const TENDER_DOC_KIND_LABELS: Record<TenderDocKind, string> = {
  gaeb: 'GAEB-LV',
  supplier_quote: 'Lieferantenangebot',
  other: 'Sonstiges',
};

export function isTenderGaebDocument(filename: string, mimeType: string | null): boolean {
  const ext = (filename.split('.').pop() || '').toLowerCase();
  const m = String(mimeType ?? '').toLowerCase();
  if (['x80', 'x81', 'x82', 'x83', 'x84', 'x85', 'x86', 'gaeb', 'xml'].includes(ext)) {
    return true;
  }
  return m.includes('xml') || m.includes('gaeb');
}

/** GAEB 90 / 2000 im Festformat: kann nicht gelesen werden. */
export function isGaeb90Filename(filename: string): boolean {
  const ext = (filename.split('.').pop() || '').toLowerCase();
  return /^[dp]8[0-9]$/.test(ext);
}

export const GAEB90_UNSUPPORTED_MESSAGE =
  'GAEB 90 / GAEB 2000 (.d83, .p83) wird nicht unterstützt. Bitte die Ausschreibung als GAEB DA XML (.x83) anfordern.';

export const DEFAULT_LABOR_RATE = 75;
export const DEFAULT_REAL_LABOR_RATE = 55;
export const DEFAULT_VAT_RATE = 19;
export const DEFAULT_MATERIAL_MARKUP_RATE = 0;
